import { Select } from 'antd'
import clsx from 'clsx'
import { forwardRef, useEffect, useMemo, useRef } from 'react'

import { DESCRIPTIONS } from '@/constants/common/message.constants'
import { useSubPartnersSelectQuery } from '@/features/partner-to-sub-partners/hooks'
import { PARTNER_TIER_LABEL, type PartnerTier } from '@/libs/api/types'

type Props = {
  tenantKey: string
  dcId?: number
  partnerId?: number
  value?: PartnerTier | null
  onChange?: (value: PartnerTier | null) => void
  className?: string
  disabled?: boolean
  allowClear?: boolean
  placeholder?: string
  subPartnerId?: number | null
}

export const PartnerTierSelect = forwardRef(
  ({
    tenantKey,
    dcId,
    partnerId,
    subPartnerId,
    value,
    onChange,
    className,
    disabled,
    allowClear = true,
    placeholder = DESCRIPTIONS.SELECT_SUBCONTRACTING_HIERARCHY
  }: Props) => {
    const PARTNER_TIER_OPTIONS = useMemo(
      () =>
        Object.entries(PARTNER_TIER_LABEL).map(([value, label]) => ({
          value,
          label
        })),
      []
    )

    // サブパートナーIDからpartnerTierを取得
    const { data } = useSubPartnersSelectQuery(
      {
        tenantKey,
        query: { partnerId, dcId }
      },
      { disabled: !subPartnerId }
    )

    // 手動選択フラグをセット
    const isManualSelect = useRef(false)

    // サブパートナーIDが変わったら手動選択フラグをリセット
    useEffect(() => {
      isManualSelect.current = false
    }, [subPartnerId])

    // 前回のsubPartnerIdを保持
    const prevSubPartnerId = useRef<number | null | undefined>(undefined)

    useEffect(() => {
      if (prevSubPartnerId.current && subPartnerId === null && onChange) {
        onChange(null)
      }
      // subPartnerIdが変更された場合のみ自動設定
      if (
        prevSubPartnerId.current !== subPartnerId &&
        onChange &&
        subPartnerId &&
        data?.length
      ) {
        const found = data.find((item) => item.subPartnerId === subPartnerId)
        onChange(found?.partnerTier ?? null)
      }
      prevSubPartnerId.current = subPartnerId
      isManualSelect.current = false
    }, [subPartnerId, data, onChange])

    const handleChange = (v: PartnerTier | null) => {
      isManualSelect.current = true
      // クリアボタン押下時にundefinedが来るため、nullに変換している
      onChange?.(v === undefined ? null : v)
    }

    return (
      <Select
        className={clsx('w-full', className)}
        options={PARTNER_TIER_OPTIONS}
        value={value}
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled}
        onChange={handleChange}
      />
    )
  }
)
